import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ProjectServiceImpl } from "../../usecase/ServiceImpl";
import { Form } from "antd";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import { setLoading } from "../../../../../common/slice/CommonSlice";
import ErrorNotification from "../../../../../common/components/notification/ErrorNotification";
import { NOTIFICATION_TITLE } from "../../../../../common/constants/MESSAGE";
import { RootState } from "../../../../../store";
import { setIsCollapsed, setProjectChosen, setProjectListData } from "../slice/Slice";
import { ProjectManagerEntity, RangeDateType } from "../../entity/Entity";
import COMMON from "../../../../../common/constants/COMMON";

export const PARAM = {
  page: "pageProject",
  size: "sizeProject",
  keyword: "keywordProject",
  projectId: "projectId",
  startDate: "startDate",
  endDate: "endDate",
  clear: "clear",
  collapse: "collapse",
  projectName: "projectName",
};

export const DEFAULT_PARAM = {
  page: 1,
  size: 11,
  keyword: "",
  projectId: 0,
  startDate: moment().startOf("month"),
  endDate: moment(),
  clear: "0",
  collapse: "0",
};

const ProjectHandler = (service: ProjectServiceImpl) => {
  const dispatch = useDispatch();

  const [form] = Form.useForm();
  const [total, setTotal] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();
  const page = +(searchParams.get(PARAM.page) || DEFAULT_PARAM.page);
  const size = +(searchParams.get(PARAM.size) || DEFAULT_PARAM.size);
  const keyword = searchParams.get(PARAM.keyword) || DEFAULT_PARAM.keyword;
  const projectId = +(
    searchParams.get(PARAM.projectId) || DEFAULT_PARAM.projectId
  );
  const startDate = moment(
    searchParams.get(PARAM.startDate) || DEFAULT_PARAM.startDate
  );
  const endDate = moment(
    searchParams.get(PARAM.endDate) || DEFAULT_PARAM.endDate
  );
  const clear = +(searchParams.get(PARAM.clear) || DEFAULT_PARAM.clear);
  const collapse = +(
    searchParams.get(PARAM.collapse) || DEFAULT_PARAM.collapse
  );

  const [projects, setProjects] = useState([]);
  // Temporary range date for UI (when user selects date)
  const [tempRangeDate, setTempRangeDate] = useState<RangeDateType>({
    from: startDate,
    to: endDate,
  });
  // Applied range date for API (when user clicks Filter button)
  const [appliedRangeDate, setAppliedRangeDate] = useState<RangeDateType>({
    from: startDate,
    to: endDate,
  });

  const projectChosen = useSelector(
    (state: RootState) => state.am005.projectChosen
  );

  // Sync appliedRangeDate with URL params (for search and filter)
  useEffect(() => {
    const hasDateParams = searchParams.has(PARAM.startDate) || searchParams.has(PARAM.endDate);
    if (!hasDateParams && !clear) {
      // No date params and not cleared = initial load with defaults
      setAppliedRangeDate({
        from: DEFAULT_PARAM.startDate,
        to: DEFAULT_PARAM.endDate,
      });
      setTempRangeDate({
        from: DEFAULT_PARAM.startDate,
        to: DEFAULT_PARAM.endDate,
      });
    } else if (clear) {
      // Clear flag set = reset to default
      setAppliedRangeDate({
        from: DEFAULT_PARAM.startDate,
        to: DEFAULT_PARAM.endDate,
      });
      setTempRangeDate({
        from: DEFAULT_PARAM.startDate,
        to: DEFAULT_PARAM.endDate,
      });
    } else {
      // Has date params = use them
      setAppliedRangeDate({
        from: startDate,
        to: endDate,
      });
      setTempRangeDate({
        from: startDate,
        to: endDate,
      });
    }
  }, [startDate?.format(COMMON.FORMAT_DATE), endDate?.format(COMMON.FORMAT_DATE), clear]);

  const getListProject = async (params: {
    page: number;
    size: number;
    sortBy?: string;
    sortType?: string;
    keyword?: string;
    from?: string;
    to?: string;
  }) => {
    try {
      dispatch(setLoading(true));
      const response = await service.getListProject({ ...params, hasTimesheets: true });
      const projectData = response?.results ?? [];
      setProjects(projectData);
      dispatch(setProjectListData(projectData))
      setTotal(response.pagination?.count);
      if (projectData?.length > 0 && !projectId) {
        searchParams.set(PARAM.projectId, projectData[0]?.id);
        searchParams.set(PARAM.projectName, projectData[0]?.projectName);
        setSearchParams(searchParams);
        dispatch(setProjectChosen(projectData[0]));
      }
    } catch (error: any) {
      ErrorNotification(error?.message || NOTIFICATION_TITLE.ERROR);
    } finally {
      dispatch(setLoading(false));
    }
  };

  const initiateFilter = () => {
    form.setFields([
      {
        name: "startDate",
        value: clear ? null : startDate,
      },
      {
        name: "endDate",
        value: endDate,
      },
      {
        name: "text",
        value: keyword,
      },
    ]);
    if (!keyword) {
      searchParams.delete(PARAM.keyword);
      setSearchParams(searchParams);
    }
    dispatch(setIsCollapsed(collapse));
  };

  var myVar: any;
  const onSearch = () => {
    if (myVar) clearTimeout(myVar);
    myVar = setTimeout(function () {
      const searchText = form.getFieldValue("text");
      searchParams.set(PARAM.keyword, searchText.trim());
      form.setFieldValue("text", searchText.trim());
      searchParams.delete(PARAM.page);
      // Keep date filters when searching
      setSearchParams(searchParams);
    }, 1000);
  };

  const onChooseProject = (project: ProjectManagerEntity) => {
    searchParams.set(PARAM.projectId, `${project?.id}`);
    searchParams.set(PARAM.projectName, `${project?.projectName}`);
    setSearchParams(searchParams);
    dispatch(setProjectChosen(project));
  };

  const onPageChange = (value: any) => {
    searchParams.set(PARAM.page, value);
    setSearchParams(searchParams);
  };

  const onPageSizeChange = (value: any) => {
    searchParams.set(PARAM.size, value);
    searchParams.delete(PARAM.size);
    setSearchParams(searchParams);
  };

  const onFilter = () => {
    searchParams.set("pageDate", COMMON.DEFAULT_PAGE + "");
    const startDate = form.getFieldValue(PARAM.startDate);
    const endDate = form.getFieldValue(PARAM.endDate);
    searchParams.delete(PARAM.startDate);
    searchParams.delete(PARAM.endDate);
    searchParams.delete(PARAM.clear);
    if (
      startDate &&
      moment(startDate).format(COMMON.FORMAT_DATE) !==
      moment(DEFAULT_PARAM.startDate).format(COMMON.FORMAT_DATE)
    ) {
      searchParams.set(
        PARAM.startDate,
        moment(startDate).format(COMMON.FORMAT_DATE)
      );
    }

    if (!startDate) {
      searchParams.set(PARAM.clear, "1");
    }

    if (
      endDate &&
      moment(endDate).format(COMMON.FORMAT_DATE) !==
      moment(DEFAULT_PARAM.endDate).format(COMMON.FORMAT_DATE)
    ) {
      searchParams.set(
        PARAM.endDate,
        moment(endDate).format(COMMON.FORMAT_DATE)
      );
    }
    // Apply the temporary range date to trigger API call
    setAppliedRangeDate({
      from: startDate,
      to: endDate,
    });
    setSearchParams(searchParams);
  };

  const onResetFilter = () => {
    form.setFieldsValue({
      startDate: DEFAULT_PARAM.startDate,
      endDate: DEFAULT_PARAM.endDate,
      keyword,
    });
    searchParams.delete(PARAM.startDate);
    searchParams.delete(PARAM.endDate);
    searchParams.delete(PARAM.clear);
    // Reset both temporary and applied range dates
    setTempRangeDate({
      from: DEFAULT_PARAM.startDate,
      to: DEFAULT_PARAM.endDate,
    });
    setAppliedRangeDate({
      from: DEFAULT_PARAM.startDate,
      to: DEFAULT_PARAM.endDate,
    });
    setSearchParams(searchParams);
  };

  const onCollapse = (value: boolean) => {
    dispatch(setIsCollapsed(value));
    searchParams.delete(PARAM.collapse);
    if (value) searchParams.set(PARAM.collapse, "1");
    setSearchParams(searchParams);
  };

  const onFromDateChange = (value: any) => {
    // Only update temporary range date, not applied range date
    setTempRangeDate({ ...tempRangeDate, from: value });
  };
  const onToDateChange = (value: any) => {
    // Only update temporary range date, not applied range date
    setTempRangeDate({ ...tempRangeDate, to: value });
  };

  return {
    onChooseProject,
    onSearch,
    onPageChange,
    onPageSizeChange,
    onFilter,
    onResetFilter,
    onCollapse,
    initiateFilter,
    onFromDateChange,
    onToDateChange,
    //api
    getListProject,

    form,
    total,
    page,
    size,
    keyword,
    projects,
    projectId,
    projectChosen,
    rangeDate: tempRangeDate, // For UI
    appliedRangeDate, // For API
    startDate,
    endDate,
  };
};

export default ProjectHandler;
